import { useMemo, useState } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import type {
  HierarchyChip,
  VenueHierarchy,
} from '@/features/billboards/types/hierarchy';
import type { LocalityPlace } from '@/features/billboards/components/list/LocalityRadiusField';
import type {
  ScreenFilters,
  ScreenOrientationFilter,
} from '../ScreensStep.types';
import {
  LocationVenueFilterModal,
  type LocationVenueFilterDraft,
} from './LocationVenueFilterModal';

type ScreenSearchFiltersProps = {
  search: string;
  areaQuery: string;
  localityPlace: LocalityPlace;
  city?: string;
  onSearchChange: (value: string) => void;
  onApplyLocationFilters: (draft: LocationVenueFilterDraft) => void;
  onClearFilters: () => void;
  filters: ScreenFilters;
  activeFilterCount: number;
  locationTypes: HierarchyChip[];
  hierarchy?: VenueHierarchy;
  venuesLoading: boolean;
  creativeOrientation?: string;
  orientationFilter: ScreenOrientationFilter;
  onOrientationFilterChange: (filter: ScreenOrientationFilter) => void;
  orientationCounts?: {
    total: number;
    matching: number;
    portrait: number;
    landscape: number;
  };
};

type FilterTab = {
  value: ScreenOrientationFilter;
  label: string;
  count?: number;
};

type ActiveChip = {
  id: string;
  label: string;
};

export function ScreenSearchFilters({
  search,
  areaQuery,
  localityPlace,
  city,
  onSearchChange,
  onApplyLocationFilters,
  onClearFilters,
  filters,
  activeFilterCount,
  locationTypes,
  hierarchy,
  venuesLoading,
  creativeOrientation,
  orientationFilter,
  onOrientationFilterChange,
  orientationCounts,
}: ScreenSearchFiltersProps) {
  const [modalOpen, setModalOpen] = useState(false);

  const hasCreativeMatch =
    creativeOrientation === 'PORTRAIT' || creativeOrientation === 'LANDSCAPE';

  const tabs: FilterTab[] = [
    {
      value: 'ALL',
      label: 'All',
      count: orientationCounts?.total,
    },
    ...(hasCreativeMatch
      ? [
          {
            value: 'MATCHING' as const,
            label: 'Matching',
            count: orientationCounts?.matching,
          },
        ]
      : []),
    {
      value: 'PORTRAIT',
      label: 'Portrait',
      count: orientationCounts?.portrait,
    },
    {
      value: 'LANDSCAPE',
      label: 'Landscape',
      count: orientationCounts?.landscape,
    },
  ];

  const activeChips = useMemo(() => {
    const chips: ActiveChip[] = [];
    if (localityPlace.label?.trim()) {
      chips.push({
        id: 'locality',
        label: `Near ${localityPlace.label.trim()}${
          localityPlace.radius ? ` · ${localityPlace.radius} km` : ''
        }`,
      });
    }
    if (areaQuery.trim()) {
      chips.push({ id: 'area', label: `Area: ${areaQuery.trim()}` });
    }
    for (const t of filters.locationType) {
      chips.push({ id: `loc-${t}`, label: t });
    }
    for (const t of filters.venueType) {
      chips.push({ id: `ven-${t}`, label: t });
    }
    for (const t of filters.venueSubType) {
      chips.push({ id: `sub-${t}`, label: t });
    }
    return chips;
  }, [localityPlace, areaQuery, filters]);

  return (
    <div className="flex flex-col gap-2.5">
      {/* Unified search + location filters toolbar */}
      <div className="flex flex-col gap-2 rounded-[16px] border border-black/[0.06] bg-white p-2 shadow-[0_1px_2px_rgba(16,38,73,0.04)] sm:flex-row sm:items-center">
        <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-[#8E98A8]"
            aria-hidden
          />
          <input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search screens, venues, or areas"
            className="h-10 w-full rounded-[12px] border-0 bg-[#F2F4F7] py-2 pr-9 pl-10 text-[13px] text-[var(--dash-ink)] outline-none placeholder:text-[#8E98A8] transition focus:bg-white focus:ring-2 focus:ring-[var(--dash-brand)]/20"
            aria-label="Search screens"
          />
          {search ? (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full p-1 text-[#8E98A8] transition hover:bg-black/[0.05] hover:text-[var(--dash-ink)]"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>

        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className={`inline-flex h-10 shrink-0 items-center gap-1.5 rounded-[12px] border px-3 text-[12px] font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--dash-brand)] ${
            activeFilterCount > 0
              ? 'border-[var(--dash-brand)]/35 bg-[var(--dash-brand)]/[0.08] text-[var(--dash-brand)]'
              : 'border-black/[0.08] bg-[#F7F8FA] text-[var(--dash-ink)] hover:bg-white'
          }`}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Location Filters</span>
          <span className="sm:hidden">Location</span>
          {activeFilterCount > 0 ? (
            <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--dash-brand)] px-1 text-[10px] font-bold text-white">
              {activeFilterCount}
            </span>
          ) : null}
        </button>
      </div>

      {/* Orientation segmented control */}
      <div
        role="radiogroup"
        aria-label="Screen orientation filter"
        className="inline-flex max-w-full flex-wrap gap-0.5 rounded-[12px] bg-[#EEF1F5] p-1 sm:flex-nowrap"
      >
        {tabs.map((tab) => {
          const active = orientationFilter === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onOrientationFilterChange(tab.value)}
              className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-[9px] px-2.5 py-1.5 text-[12px] font-semibold transition sm:flex-none ${
                active
                  ? 'bg-white text-[var(--dash-ink)] shadow-[0_1px_3px_rgba(16,38,73,0.12)]'
                  : 'text-[var(--dash-muted)] hover:text-[var(--dash-ink)]'
              }`}
            >
              <span>{tab.label}</span>
              {typeof tab.count === 'number' ? (
                <span
                  className={`tabular-nums text-[11px] ${
                    active ? 'text-[var(--dash-muted)]' : 'text-[#9CA3AF]'
                  }`}
                >
                  {tab.count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {activeChips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {activeChips.map((chip) => (
            <span
              key={chip.id}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-[var(--dash-brand)]/[0.08] py-1 pr-1 pl-2.5 text-[11px] font-semibold text-[var(--dash-brand)]"
            >
              <span className="truncate">{chip.label}</span>
            </span>
          ))}
          <button
            type="button"
            onClick={onClearFilters}
            className="rounded-full px-2 py-1 text-[11px] font-semibold text-[var(--dash-muted)] transition hover:bg-black/[0.04] hover:text-rose-600"
          >
            Clear filters
          </button>
        </div>
      ) : null}

      <LocationVenueFilterModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onApply={onApplyLocationFilters}
        localityPlace={localityPlace}
        city={city}
        areaQuery={areaQuery}
        filters={filters}
        activeFilterCount={activeFilterCount}
        locationTypes={locationTypes}
        hierarchy={hierarchy}
        venuesLoading={venuesLoading}
      />
    </div>
  );
}
